export const ROLE_SUMMARY_ROLES = [
  { key: "super_admin", label: "Super Admin" },
  { key: "admin", label: "Admin" },
  { key: "staff", label: "Staff" },
  { key: "sale", label: "Sale" },
  { key: "accountant", label: "Accountant" },
  { key: "sleep_technician", label: "Sleep Technician" },
  { key: "sleep_physician", label: "Sleep Physician" },
  { key: "patient", label: "Patient" },
];

export const ROLE_SUMMARY_REVIEWED_ON = "2026-11-03";

export const ROLE_SUMMARY_HIDDEN_ROLES = [
  "staff",
  "sleep_physician",
  "sleep_technician",
];

export const ROLE_SUMMARY_ACCESS_LEVELS = {
  can: { label: "Can use", variant: "success" },
  limited: { label: "Limited", variant: "warning" },
  read_only: { label: "Read only", variant: "info" },
  conditional: { label: "If configured", variant: "secondary" },
  none: { label: "Cannot use", variant: "danger" },
};

const SUPER_ADMIN = "super_admin";
const ADMIN = "admin";
const STAFF = "staff";
const SALE = "sale";
const ACCOUNTANT = "accountant";
const TECHNICIAN = "sleep_technician";
const PHYSICIAN = "sleep_physician";
const PATIENT = "patient";

const permission = (level, detail, label) => ({ level, detail, label });
const can = (detail, label) => permission("can", detail, label);
const limited = (detail, label) => permission("limited", detail, label);
const readOnly = (detail) => permission("read_only", detail);
const conditional = (detail) => permission("conditional", detail);
const cannot = (detail = "Not shown in the normal menu for this role.") =>
  permission("none", detail);

const grant = (roles, rolePermission) =>
  roles.reduce((result, role) => {
    result[role] = rolePermission;
    return result;
  }, {});

const mergeAccess = (...groups) => Object.assign({}, ...groups);

// These Admin menus depend on the account's customer assignments in SideBar.
// This list documents that behavior; it does not grant or revoke access.
const CUSTOMER_RESTRICTED_ADMIN_MENUS = new Set([
  "/tech-dashboard", "/sleep-study-monitor", "/sleep-study-monitor-flow",
  "/booking-urgent", "/technician-schedule", "/doctor-schedule", "/booking-recovery",
  "/cpap-trial-service-list", "/cpap-trial-recovery", "/product-list",
  "/sale-order-list", "/new-order", "/product-register", "/accessories-list",
  "/material-issue-list", "/stock-logs-list", "/sale-package-list", "/item-list",
  "/mask-list", "/cpap-service", "/tech-list", "/sale-list", "/doctor-list",
  "/accountant-list", "/approver-list", "/feature-request-list",
]);

const definePage = ({ section, page, path, access }) => {
  const normalizedAccess = ROLE_SUMMARY_ROLES.reduce((result, role) => {
    result[role.key] = access[role.key] || cannot();
    return result;
  }, {});

  if (CUSTOMER_RESTRICTED_ADMIN_MENUS.has(path) && normalizedAccess[ADMIN].level !== "none") {
    normalizedAccess[ADMIN] = conditional(
      `${normalizedAccess[ADMIN].detail} Menu hidden when customer access is restricted.`,
    );
  }

  return {
    id: `${section}-${page}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, ""),
    section,
    page,
    path,
    access: normalizedAccess,
  };
};

const salesWorkspaceAccess = mergeAccess(
  grant([SUPER_ADMIN], can("Global data and all normal page actions.")),
  grant([ADMIN], limited("Use the existing admin workflow and page actions.")),
  grant([SALE], can("Use the existing sale workflow and page actions.")),
  grant(
    [ACCOUNTANT],
    can("Same workflow actions as Sale, with all-node data."),
  ),
);

const cpapInventoryAccess = mergeAccess(
  grant([SUPER_ADMIN], can("Global CPAP inventory access.")),
  grant([SALE], can("Use the existing sale CPAP workflow.")),
  grant(
    [ACCOUNTANT],
    can("Same actions as Sale, with all-node visibility."),
  ),
);

const itemsMasterAccess = mergeAccess(
  grant([SUPER_ADMIN], can("View and maintain master data.")),
  grant([ADMIN], can("View and maintain master data.")),
);

const equipmentAccess = grant(
  [SUPER_ADMIN],
  can("View and maintain this equipment workspace."),
);

const cpapExportAccess = mergeAccess(
  grant([SUPER_ADMIN], can("Export global data.")),
  grant([SALE], can("Export all-node data using the selected range and filters.")),
  grant([ACCOUNTANT], can("Export all-node data.")),
);

export const ROLE_SUMMARY_PAGES = [
  definePage({
    section: "Dashboard",
    page: "Dashboard",
    path: "/dashboard",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("View global operational totals and lists.")),
      grant([ADMIN], limited("View metrics for assigned nodes and configured customers.")),
      grant(
        [STAFF],
        limited("View assigned-node and configured-customer metrics."),
      ),
      grant([SALE], can("View the existing sale dashboard metrics.")),
      grant([ACCOUNTANT], can("View all-node sale dashboard metrics.")),
      grant([TECHNICIAN], limited("View technician workload metrics.")),
      grant(
        [PHYSICIAN],
        limited("Basic dashboard only; use Doctor Dashboard for own cases."),
      ),
    ),
  }),
  definePage({
    section: "Dashboard",
    page: "Technician Dashboard",
    path: "/tech-dashboard",
    access: mergeAccess(
      grant([SUPER_ADMIN], limited("All-technician overview; full payment access is required for all-technician payment records and confirmation. Otherwise uses the mapped or own provider scope.")),
      grant([ADMIN], limited("Mapped or own technician scope, including own pending batches and paid cases.")),
      grant([TECHNICIAN], limited("Mapped or own technician scope, including own pending batches and paid cases.")),
      grant(
        [STAFF, SALE, ACCOUNTANT, PHYSICIAN],
        conditional("Requires full payment access or an email-to-provider mapping; the mapping limits the provider scope."),
      ),
      grant([PATIENT], conditional("Requires explicit payment access or an email-to-provider mapping.")),
    ),
  }),
  definePage({
    section: "Dashboard",
    page: "Doctor Dashboard",
    path: "/doctor-dashboard",
    access: mergeAccess(
      grant([SUPER_ADMIN], limited("Mapped or own physician scope; all-physician payment access requires a full payment-access grant.")),
      grant([PHYSICIAN], limited("Mapped or own physician scope; full payment access can expand the scope.")),
      grant(
        [ADMIN, STAFF, SALE, ACCOUNTANT, TECHNICIAN],
        conditional("Requires full payment access or an email-to-provider mapping; the mapping limits the provider scope."),
      ),
      grant([PATIENT], conditional("Requires explicit payment access or an email-to-provider mapping.")),
    ),
  }),
  definePage({
    section: "Header",
    page: "Top-right Case Notification",
    path: "Header",
    access: mergeAccess(
      grant(
        [SUPER_ADMIN, ADMIN, STAFF],
        limited("Shown using the existing notification query for this role."),
      ),
      grant(
        [SALE],
        limited("Assigned nodes; LPH cases are limited to own salesperson."),
      ),
      grant(
        [ACCOUNTANT],
        cannot("Hidden and no case-notification query is performed."),
      ),
      grant([TECHNICIAN], limited("Shows own technician cases.")),
      grant([PHYSICIAN], limited("Shows own physician cases.")),
      grant([PATIENT], cannot("Not an operational patient feature.")),
    ),
  }),
  definePage({
    section: "Header",
    page: "Annual Mask Purchase Notifications",
    path: "Header",
    access: mergeAccess(
      grant(ROLE_SUMMARY_ROLES.map((role) => role.key), conditional("Requires an Annual Mask Access email grant, including Super Admin. Page access is checked separately.")),
      grant([SALE], conditional("Requires an Annual Mask Access email grant; notifications cover assigned nodes only.")),
    ),
  }),
  definePage({
    section: "Header",
    page: "Patient Ticket Notifications",
    path: "Header",
    access: mergeAccess(
      grant(ROLE_SUMMARY_ROLES.map((role) => role.key), conditional("Requires an email grant for each ticket type. View notifications; the management page is Super Admin only.")),
      grant([SUPER_ADMIN], conditional("Requires an email grant for each ticket type; notifications link to ticket management.")),
    ),
  }),
  definePage({
    section: "Account",
    page: "Your Account",
    path: "/",
    access: grant(
      ROLE_SUMMARY_ROLES.map((role) => role.key),
      can("View and update the signed-in account profile."),
    ),
  }),
  definePage({
    section: "Account",
    page: "Accounts",
    path: "/admin-list",
    access: grant(
      [SUPER_ADMIN],
      can("Create accounts and change another account's role."),
    ),
  }),
  definePage({
    section: "Account",
    page: "Payment Access",
    path: "/payment-access-config",
    access: mergeAccess(
      grant([SUPER_ADMIN], conditional("Requires full payment access. Manage provider mappings; only admin888 can change full-access emails and the record-payment note check.")),
      grant(
        [TECHNICIAN, PHYSICIAN],
        conditional("Requires full payment access to manage provider mappings; full-access email changes remain admin888-only."),
      ),
    ),
  }),
  definePage({
    section: "Account",
    page: "Recovery Delete Access",
    path: "/recovery-delete-access",
    access: grant(
      [SUPER_ADMIN],
      can("Configure protected recovery/delete access."),
    ),
  }),
  definePage({
    section: "Account",
    page: "Annual Mask Access",
    path: "/annual-mask-access",
    access: grant([SUPER_ADMIN], can("Manage email grants for annual mask purchase notifications and order access.")),
  }),
  definePage({
    section: "Account",
    page: "Email Sender Settings",
    path: "/email-sender-settings",
    access: grant([SUPER_ADMIN], can("Configure sender connections and node/customer assignments separately for Annual Mask and Booking doctor emails.")),
  }),
  definePage({
    section: "Account",
    page: "Patient Portal Ticket Access",
    path: "/patient-portal-ticket-access",
    access: grant([SUPER_ADMIN], can("Manage ticket-type email grants and whether patients may book a sleep test without a customer link.")),
  }),
  definePage({
    section: "Account",
    page: "Summarize Role",
    path: "/summarize-role",
    access: mergeAccess(
      grant(
        [SUPER_ADMIN, ADMIN, SALE, ACCOUNTANT, PATIENT],
        can("View the page-by-role access summary."),
      ),
      grant(
        ROLE_SUMMARY_HIDDEN_ROLES,
        cannot("The link and route are hidden for this role."),
      ),
    ),
  }),
  definePage({
    section: "Account",
    page: "Create Account Guide",
    path: "/create-account-guide",
    access: grant(
      [SUPER_ADMIN],
      readOnly("Read account setup instructions for doctors, technicians, and staff."),
    ),
  }),
  definePage({
    section: "Patient Tickets",
    page: "Patient Tickets",
    path: "/patient-portal-tickets",
    access: grant([SUPER_ADMIN], conditional("Requires email access for each ticket type, even for Super Admin. Review requests, update statuses, and register patients from eligible tickets.")),
  }),
  definePage({
    section: "Patient Tickets",
    page: "Complaints",
    path: "/patient-portal-complaints",
    access: grant([SUPER_ADMIN], can("Review complaints, assign follow-up, update progress, and send apology emails.")),
  }),
  definePage({
    section: "Node",
    page: "Our Nodes",
    path: "/node-list",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("Maintain nodes, customers, services, and availability; share customer booking links and QR codes.")),
      grant([ADMIN, STAFF], readOnly("View assigned nodes and configured customers; share customer booking links and QR codes. Cannot maintain node/customer records.")),
    ),
  }),
  definePage({
    section: "Patient",
    page: "Search Patient",
    path: "/patient-list",
    access: mergeAccess(
      grant([SUPER_ADMIN, SALE, ACCOUNTANT], can("Search patients globally.")),
      grant([ADMIN], limited("Search patients in assigned nodes and configured customers.")),
      grant(
        [STAFF],
        limited("Assigned nodes and configured customers only."),
      ),
    ),
  }),
  definePage({
    section: "Patient",
    page: "Incoming Patients",
    path: "/patient-incoming",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("View and manage all incoming patients, check duplicates, and assign or correct HN.")),
      grant([ADMIN], limited("Assigned nodes and configured customers; create/edit incoming records. HN assignment and correction are Super Admin only.")),
      grant(
        [STAFF],
        limited("Assigned node/customer incoming patients; no create button."),
      ),
    ),
  }),
  definePage({
    section: "Patient",
    page: "New Patient",
    path: "/patient-register",
    access: grant(
      [SUPER_ADMIN, ADMIN],
      can("Create a new patient record."),
    ),
  }),
  definePage({
    section: "Patient",
    page: "Share Patient Portal",
    path: "Patient menu dialog",
    access: grant([SUPER_ADMIN, ADMIN, STAFF, SALE], can("Open the Patient Portal sharing dialog and copy its link or QR code.")),
  }),
  definePage({
    section: "Patient",
    page: "Patient Detail",
    path: "/patient-page/:id",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("Global patient tabs, forms, notes, and files.")),
      grant([ADMIN], limited("Patient management within assigned nodes and configured customers.")),
      grant(
        [STAFF],
        limited("Assigned patient access; form deletion is restricted."),
      ),
      grant(
        [SALE],
        can("Global patient view with sale-level tabs, notes, and files."),
      ),
      grant(
        [ACCOUNTANT],
        can("Same patient actions as Sale, with global visibility."),
      ),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Calendar",
    path: "/booking-calendar",
    access: mergeAccess(
      grant(
        [SUPER_ADMIN],
        can("All nodes; create/edit and protected delete/move workflows."),
      ),
      grant(
        [ADMIN],
        limited("Assigned nodes and configured customers; create/edit and protected delete/move."),
      ),
      grant(
        [STAFF],
        limited("Assigned node/customer data; cannot create or upload files."),
      ),
      grant(
        [SALE],
        limited(
          "Assigned nodes and own LPH salesperson; no create, report email, or protected file actions.",
        ),
      ),
      grant(
        [ACCOUNTANT],
        limited(
          "All nodes and all salespeople; keeps Sale create/file/email restrictions.",
        ),
      ),
      grant(
        [TECHNICIAN],
        limited("Own technician cases; no create or protected delete/move."),
      ),
      grant([PHYSICIAN], limited("Own physician cases and doctor workflow.")),
      grant(
        [PATIENT],
        cannot("Legacy menu entry is not an intended patient workflow."),
      ),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Table",
    path: "/booking-table",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("All-node booking table and management.")),
      grant([ADMIN], limited("Assigned-node and configured-customer booking table, Excel export, and management.")),
      grant(
        [STAFF],
        limited("Assigned nodes and configured customers; restricted actions."),
      ),
      grant(
        [SALE],
        limited("Assigned nodes and own LPH salesperson; sale-level actions."),
      ),
      grant(
        [ACCOUNTANT],
        limited("All nodes and all salespeople; sale-level actions."),
      ),
      grant([TECHNICIAN], limited("Own technician cases.")),
      grant([PHYSICIAN], limited("Own physician cases.")),
      grant(
        [PATIENT],
        cannot("Legacy menu entry is not an intended patient workflow."),
      ),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Sleep Study Monitor",
    path: "/sleep-study-monitor",
    access: grant(
      [SUPER_ADMIN, ADMIN],
      can("Manage center/client handoffs and exact workflow statuses."),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Sleep Study Guide",
    path: "/sleep-study-monitor-flow",
    access: grant(
      [SUPER_ADMIN, ADMIN],
      can("Open the operator workflow guide."),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Urgent Cases",
    path: "/booking-urgent",
    access: mergeAccess(
      grant(
        [SUPER_ADMIN, ADMIN, SALE, ACCOUNTANT],
        can("View all-node urgent cases under this page's current behavior."),
      ),
      grant([TECHNICIAN], limited("View own recording-technician cases.")),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Technician Schedule",
    path: "/technician-schedule",
    access: mergeAccess(
      grant(
        [SUPER_ADMIN, ADMIN, SALE, ACCOUNTANT],
        can("View the technician schedule workspace."),
      ),
      grant([TECHNICIAN], limited("View the signed-in technician schedule.")),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Doctor Schedule",
    path: "/doctor-schedule",
    access: mergeAccess(
      grant([SUPER_ADMIN, ADMIN, SALE], can("View the schedule and create, edit, or delete doctor OPD visits.")),
      grant([ACCOUNTANT, TECHNICIAN, PHYSICIAN], readOnly("View doctor OPD dates, times, nodes, and customers.")),
    ),
  }),
  definePage({
    section: "Booking",
    page: "Recovery & Delete Log",
    path: "/booking-recovery",
    access: grant(
      [SUPER_ADMIN, ADMIN],
      limited("Inspect and restore booking recovery records. Permanent deletion requires Recovery Delete Access; bulk empty-HN cleanup is Super Admin only."),
    ),
  }),
  definePage({
    section: "CPAP Trial",
    page: "Calendar",
    path: "/trial-calendar",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("All nodes and all Calendar actions.")),
      grant([ADMIN], limited("Assigned nodes and configured customers with admin Calendar actions.")),
      grant([STAFF], readOnly("Assigned-node and configured-customer Calendar view.")),
      grant([SALE], limited("Assigned nodes with sale Calendar actions.")),
      grant(
        [ACCOUNTANT],
        can("All nodes with the same Calendar actions as Sale."),
      ),
    ),
  }),
  definePage({
    section: "CPAP Trial",
    page: "Search CPAP",
    path: "/cpap-list",
    access: cpapInventoryAccess,
  }),
  definePage({
    section: "CPAP Trial",
    page: "Register CPAP",
    path: "/cpap-register",
    access: cpapInventoryAccess,
  }),
  definePage({
    section: "CPAP Trial",
    page: "Mask Inventory",
    path: "/mask-inventory",
    access: cpapInventoryAccess,
  }),
  definePage({
    section: "CPAP Trial",
    page: "Mask Transaction",
    path: "/mask-transaction",
    access: cpapInventoryAccess,
  }),
  definePage({
    section: "CPAP Trial",
    page: "Service",
    path: "/cpap-trial-service-list",
    access: mergeAccess(
      cpapInventoryAccess,
      grant([ADMIN], can("Use the CPAP Trial service workflow.")),
    ),
  }),
  definePage({
    section: "CPAP Trial",
    page: "External Repair",
    path: "/cpap-trial-external-repair-list",
    access: cpapInventoryAccess,
  }),
  definePage({
    section: "CPAP Trial",
    page: "Recovery & Delete Log",
    path: "/cpap-trial-recovery",
    access: grant(
      [SUPER_ADMIN, ADMIN],
      limited("Inspect and restore CPAP Trial recovery records. Permanent deletion requires Recovery Delete Access; bulk empty-HN cleanup is Super Admin only."),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Follow-Up Dashboard",
    path: "/cpap-followup-dashboard",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("Global current-month follow-up dashboard.")),
      grant([ADMIN], limited("Assigned-node and configured-customer current-month dashboard.")),
      grant([SALE], limited("Assigned-node current-month dashboard.")),
      grant([ACCOUNTANT], can("All-node current-month dashboard.")),
      grant([PHYSICIAN], limited("Cases assigned to this doctor for consultation.")),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Follow-Up Calendar",
    path: "/cpap-followup-calendar",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("Global follow-up management.")),
      grant([ADMIN], limited("Follow-up management within assigned nodes and configured customers.")),
      grant(
        [STAFF],
        readOnly("Assigned node/customer follow-ups; writes are blocked."),
      ),
      grant(
        [SALE],
        limited("Assigned-node management; report/questionnaire upload blocked."),
      ),
      grant(
        [ACCOUNTANT],
        limited("All-node management; report/questionnaire upload blocked."),
      ),
      grant([PHYSICIAN], limited("Own cases and doctor actions.")),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Follow-Up Table",
    path: "/cpap-followup-table",
    access: mergeAccess(
      grant([SUPER_ADMIN], can("Global full-collection follow-up table.")),
      grant([ADMIN], limited("Assigned-node and configured-customer follow-up table.")),
      grant(
        [SALE],
        limited("Assigned nodes; report/questionnaire upload blocked."),
      ),
      grant(
        [ACCOUNTANT],
        limited("All nodes; report/questionnaire upload blocked."),
      ),
      grant([PHYSICIAN], limited("Cases assigned to this doctor for consultation.")),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Follow-Up History",
    path: "/followup-record-history",
    access: mergeAccess(
      grant([SUPER_ADMIN], readOnly("View global immutable history.")),
      grant([ADMIN], readOnly("View immutable history for assigned nodes and configured customers.")),
      grant([SALE], readOnly("View the existing global sale history.")),
      grant([ACCOUNTANT], readOnly("View global immutable history.")),
      grant([PHYSICIAN], readOnly("View own physician history.")),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Annual Mask Batch Review",
    path: "/annual-mask-purchase",
    access: mergeAccess(
      grant([SUPER_ADMIN, ADMIN], can("Manage batches and all cases, documents, approvals, and the ready-to-purchase order workflow.")),
      grant([SALE], limited("View assigned-node cases. Manual order creation for ready cases requires an Annual Mask Access email grant.")),
      grant([ACCOUNTANT], limited("View all batches. Manual order creation for ready cases requires an Annual Mask Access email grant.")),
      grant([PHYSICIAN], limited("View cases assigned by doctor name or email; upload signed certificates while the case is unlocked.")),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Annual Mask Purchase Guide",
    path: "/annual-mask-purchase-guide",
    access: grant([SUPER_ADMIN, ADMIN, SALE, ACCOUNTANT, PHYSICIAN], readOnly("Read annual mask batch, certificate, approval, and purchase instructions.")),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Follow-Up Guide",
    path: "/cpap-followup-guide",
    access: grant(
      [SUPER_ADMIN, ADMIN, STAFF, SALE, ACCOUNTANT, PHYSICIAN],
      can("Open the CPAP Follow-Up workflow guide."),
    ),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Legacy Calendar",
    path: "/followup-calendar",
    access: grant([SUPER_ADMIN], can("Use the legacy follow-up Calendar.")),
  }),
  definePage({
    section: "CPAP Follow-Up",
    page: "Legacy Table",
    path: "/followup-table",
    access: grant([SUPER_ADMIN], can("Use the legacy follow-up Table.")),
  }),
  ...[
    ["Product List", "/product-list"],
    ["New Order List", "/sale-order-list"],
    ["Create Order", "/new-order"],
    ["Product Register", "/product-register"],
    ["Accessories", "/accessories-list"],
    ["Material Issue", "/material-issue-list"],
    ["Stock Logs", "/stock-logs-list"],
  ].map(([page, path]) =>
    definePage({
      section: "Sales",
      page,
      path,
      access: salesWorkspaceAccess,
    }),
  ),
  ...[
    ["Item List", "/item-list"],
    ["Sale Package List", "/sale-package-list"],
    ["Mask List", "/mask-list"],
    ["CPAP Service", "/cpap-service"],
    ["Technician List", "/tech-list"],
    ["Sale List", "/sale-list"],
    ["Doctor List", "/doctor-list"],
    ["Accountant List", "/accountant-list"],
    ["Approver List", "/approver-list"],
  ].map(([page, path]) =>
    definePage({
      section: "Items",
      page,
      path,
      access: itemsMasterAccess,
    }),
  ),
  definePage({
    section: "Product Requests",
    page: "Request List",
    path: "/feature-request-list",
    access: grant(
      [SUPER_ADMIN, ADMIN, SALE, ACCOUNTANT, TECHNICIAN],
      limited(
        "Create requests and edit own requests; only admin888 manages all status/priority/delete actions.",
      ),
    ),
  }),
  definePage({
    section: "Export",
    page: "Sleep Study Summary",
    path: "/export",
    access: mergeAccess(
      grant([SUPER_ADMIN, SALE, ACCOUNTANT], can("Export global data.")),
      grant([ADMIN, STAFF], limited("Export data for assigned nodes and configured customers.")),
      grant([TECHNICIAN], limited("Export with recording and scoring technician filters fixed to the signed-in technician.")),
      grant([PHYSICIAN], limited("Export with the doctor filter fixed to the signed-in physician.")),
      grant(
        [PATIENT],
        cannot("Legacy menu entry is not an intended patient export."),
      ),
    ),
  }),
  definePage({
    section: "Export",
    page: "Post Consult Summary",
    path: "/export",
    access: grant([SUPER_ADMIN, ADMIN], conditional("Shown when accessible nodes include LPH. Summarize and export all accessible nodes, with customer filters.")),
  }),
  definePage({
    section: "Export",
    page: "CPAP Trial Export",
    path: "/export",
    access: cpapExportAccess,
  }),
  definePage({
    section: "Export",
    page: "CPAP Sale Export",
    path: "/export",
    access: cpapExportAccess,
  }),
  definePage({
    section: "Export",
    page: "Medical Certificate Export",
    path: "/export",
    access: cpapExportAccess,
  }),
  definePage({
    section: "Service",
    page: "Customer Service",
    path: "/service-ticket-list",
    access: mergeAccess(
      grant([SUPER_ADMIN, SALE], can("Use the existing global service workflow.")),
      grant(
        [ACCOUNTANT],
        can("Same service workflow as Sale, with all-node visibility."),
      ),
    ),
  }),
  definePage({
    section: "Service",
    page: "External Repair",
    path: "/external-repair-list",
    access: mergeAccess(
      grant([SUPER_ADMIN, SALE], can("Use the existing global repair workflow.")),
      grant(
        [ACCOUNTANT],
        can("Same repair workflow as Sale, with all-node visibility."),
      ),
    ),
  }),
  ...[
    ["Equipment", "/equipment-list"],
    ["Tickets", "/ticket-list"],
    ["Sensors", "/sleep-study-sensor"],
    ["Consumable Supplies", "/sleep-study-equipment"],
  ].map(([page, path]) =>
    definePage({
      section: "Equipment",
      page,
      path,
      access: equipmentAccess,
    }),
  ),
  definePage({
    section: "Backup",
    page: "Backup",
    path: "/backup",
    access: grant([SUPER_ADMIN], can("View and run the backup workspace.")),
  }),
  definePage({
    section: "Settings",
    page: "Settings",
    path: "/settings",
    access: grant(
      [SUPER_ADMIN, SALE, ACCOUNTANT],
      can("Open and update the existing Settings page."),
    ),
  }),
];

export const ROLE_SUMMARY_SECTIONS = [
  ...new Set(ROLE_SUMMARY_PAGES.map((item) => item.section)),
];

export const canViewRoleSummary = (userOrRoles) => {
  const roles = Array.isArray(userOrRoles)
    ? userOrRoles
    : userOrRoles && Array.isArray(userOrRoles.roles)
      ? userOrRoles.roles
      : [];

  return (
    roles.length > 0 &&
    !roles.some((role) => ROLE_SUMMARY_HIDDEN_ROLES.includes(role))
  );
};
